import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import './StandingsBigScreen.css'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'

const LEAGUE_ID = '6a466e850a2277ad4c9a7210'

const DIVISION_COLUMNS = {
	girls: ['Girls U19', 'Girls U14', 'Girls U11'],
	boys: ['Boys U15', 'Boys U13', 'Boys U11'],
}

const getGamesPlayed = (team) =>
	(team.wins || 0) +
	(team.losses || 0) +
	(team.ties || 0) +
	(team.shootoutLosses || 0) +
	(team.overtimeLosses || 0)

const getWinningPercentage = (team) => {
	const gamesPlayed = getGamesPlayed(team)

	if (gamesPlayed === 0) {
		return '0.000'
	}

	return ((team.wins + team.ties * 0.5) / gamesPlayed).toFixed(3)
}

const DivisionStandingsTable = ({ divisionName, teams, year }) => {
	if (!teams || teams.length === 0) {
		return null
	}

	return (
		<section className='bigScreenDivisionCard'>
			<div className='bigScreenDivisionHeading'>
				<span className='bigScreenDivisionHeading__line' />
				<span className='bigScreenDivisionHeading__star'>★</span>

				<h2>{divisionName} Division</h2>

				<span className='bigScreenDivisionHeading__star'>★</span>
				<span className='bigScreenDivisionHeading__line' />
			</div>

			<table
				className='bigScreenStandingsTable'
				cellSpacing='0'
				cellPadding='0'
			>
				<thead>
					<tr>
						<th>Team</th>
						<th>GP</th>
						<th>W</th>
						<th>L</th>
						<th>SOL</th>
						<th>GF</th>
						<th>GA</th>
						<th>GD</th>
						<th>Points</th>
						<th>%</th>
					</tr>
				</thead>

				<tbody>
					{teams.map((team) => (
						<tr key={team._id || `${divisionName}-${team.teamName}`}>
							<td className='bigScreenStandingsTable__team'>
								<Link to={`${team.divisionName}/${year}/${team.teamName}`}>
									{team.teamName}
								</Link>
							</td>

							<td>{getGamesPlayed(team)}</td>
							<td>{team.wins}</td>
							<td>{team.losses}</td>
							<td>{team.shootoutLosses}</td>
							<td>{team.goalsFor}</td>
							<td>{team.goalsAgainst}</td>
							<td>{team.goalsFor - team.goalsAgainst}</td>
							<td>{team.points}</td>
							<td>{getWinningPercentage(team)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</section>
	)
}

const StandingsBigScreen = () => {
	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)
	const [standings, setStandings] = useState([])
	const [leagueName, setLeagueName] = useState('Future Stars')
	const [session, setSession] = useState('')
	const [year, setYear] = useState('2026')
	const [timestamp, setTimestamp] = useState(new Date())

	/* useEffect(() => {
		const intervalId = setInterval(() => {
			setTimestamp(new Date())
		}, 60000)

		return () => clearInterval(intervalId)
	}, [])

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/leagues/${LEAGUE_ID}/standings`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				const incomingStandings = Array.isArray(responseData.masterMasterArray)
					? responseData.masterMasterArray
					: []

				const teamCount =
					Number(responseData.numberOfTeamsTotal) || incomingStandings.length

				const trimmedStandings = incomingStandings.slice(-teamCount)

				setStandings(trimmedStandings)
				setLeagueName(responseData.leagueName || 'Future Stars')
				setSession(responseData.session || '')
				setYear(responseData.year || '2026')
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, []) */

	useEffect(() => {
		let isMounted = true

		const sendRequest = async () => {
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/leagues/${LEAGUE_ID}/standings`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				const incomingStandings = Array.isArray(responseData.masterMasterArray)
					? responseData.masterMasterArray
					: []

				const teamCount =
					Number(responseData.numberOfTeamsTotal) || incomingStandings.length

				const trimmedStandings = incomingStandings.slice(-teamCount)

				if (isMounted) {
					setStandings(trimmedStandings)
					setLeagueName(responseData.leagueName || 'Future Stars')
					setSession(responseData.session || '')
					setYear(responseData.year || '2026')

					// This now represents the time the standings were actually refreshed
					setTimestamp(new Date())
					setError(null)
				}
			} catch (err) {
				if (isMounted) {
					setError(err.message)
				}
			} finally {
				if (isMounted) {
					setIsLoading(false)
				}
			}
		}

		setIsLoading(true)

		// Fetch immediately when page loads
		sendRequest()

		// Then fetch fresh standings every 60 seconds
		const intervalId = setInterval(() => {
			sendRequest()
		}, 60000)

		return () => {
			isMounted = false
			clearInterval(intervalId)
		}
	}, [])

	const standingsByDivision = useMemo(() => {
		return standings.reduce((divisionMap, team) => {
			if (!divisionMap[team.divisionName]) {
				divisionMap[team.divisionName] = []
			}

			divisionMap[team.divisionName].push(team)

			return divisionMap
		}, {})
	}, [standings])

	const availableDivisionNames = Object.keys(standingsByDivision)

	const hasExpectedBigScreenDivisions = [
		...DIVISION_COLUMNS.girls,
		...DIVISION_COLUMNS.boys,
	].every((division) => standingsByDivision[division]?.length > 0)

	const errorHandler = () => {
		setError(null)
	}

	return (
		<div
			style={{
				minHeight: '100vh',
				backgroundColor: '#e0e0e0',
				overflow: 'hidden',
			}}
		>
			<ErrorModal error={error} onClear={errorHandler} />

			{isLoading && (
				<div className='center'>
					<LoadingSpinner />
				</div>
			)}

			{!isLoading && standings.length > 0 && (
				<>
					<header
						style={{
							display: 'grid',
							gridTemplateColumns: '180px 1fr 180px',
							alignItems: 'center',
							minHeight: '108px',
							padding: '12px 28px',
							backgroundColor: '#172d4b',
							borderBottom: '6px solid #b42328',
							boxSizing: 'border-box',
						}}
					>
						{/* <div className='bigScreenLogoPlaceholder'>''</div> */}
						<div></div>

						<div
							style={{
								textAlign: 'center',
							}}
						>
							<h1
								style={{
									margin: 0,
									color: '#ffffff',
									fontSize: 'clamp(2rem, 3.2vw, 4rem)',
									fontWeight: 800,
									letterSpacing: '2px',
									lineHeight: 1,
									textTransform: 'uppercase',
								}}
							>
								{year} {leagueName} Standings
							</h1>

							{session && (
								<div
									style={{
										marginTop: '8px',
										color: '#d8dee8',
										fontSize: '1.1rem',
										fontWeight: 600,
										letterSpacing: '1px',
										textTransform: 'uppercase',
									}}
								>
									{session}
								</div>
							)}
						</div>

						<div
							style={{
								color: '#ffffff',
								fontSize: '1.25rem',
								fontWeight: 700,
								textAlign: 'right',
							}}
						>
							<div>Updated</div>

							<div>
								{timestamp.toLocaleTimeString([], {
									hour: 'numeric',
									minute: '2-digit',
								})}
							</div>
						</div>
					</header>

					{hasExpectedBigScreenDivisions ? (
						<main
							style={{
								display: 'grid',
								gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
								columnGap: '5vw',
								padding: '22px 5vw 34px',
								boxSizing: 'border-box',
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '26px',
								}}
							>
								{DIVISION_COLUMNS.girls.map((division) => (
									<DivisionStandingsTable
										key={division}
										divisionName={division}
										teams={standingsByDivision[division]}
										year={year}
									/>
								))}
							</div>

							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '26px',
								}}
							>
								{DIVISION_COLUMNS.boys.map((division) => (
									<DivisionStandingsTable
										key={division}
										divisionName={division}
										teams={standingsByDivision[division]}
										year={year}
									/>
								))}
							</div>
						</main>
					) : (
						<main
							style={{
								width: '90%',
								maxWidth: '1100px',
								margin: '0 auto',
								padding: '32px 0',
							}}
						>
							{availableDivisionNames.map((division) => (
								<DivisionStandingsTable
									key={division}
									divisionName={division}
									teams={standingsByDivision[division]}
									year={year}
								/>
							))}
						</main>
					)}
				</>
			)}

			{!isLoading && !error && standings.length === 0 && (
				<div
					style={{
						padding: '60px 20px',
						textAlign: 'center',
						fontSize: '1.5rem',
						fontWeight: 700,
					}}
				>
					No standings are currently available.
				</div>
			)}
		</div>
	)
}

export default StandingsBigScreen
/* import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import './StandingsBigScreen.css'
//import StatsAndStandingsHome from '../../shared/components/Navigation/StatsAndStandingsHome'
import StatsAndStandingsHomeNavigation from '../../shared/components/Navigation/StatsAndStandingsHomeNavigation'
import Banner from '../../shared/components/Navigation/Banner'
//import { useHttpClient } from '../../shared/hooks/http-hook'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'

const StandingsBigScreen = () => {
	//const { sendRequest, clearError } = useHttpClient()
	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)
	const [loadedStandings, setLoadedStandings] = useState()
	const [numberOfTeamsTotal, setNumberOfTeamsTotal] = useState()
	const [loadedStandingsArray, setLoadedStandingsArray] = useState([])
	const [leagueName, setLeagueName] = useState()
	const [divisionName, setDivisionName] = useState()
	const [session, setSession] = useState()
	const [year, setYear] = useState()
	const [masterArray, setMasterArray] = useState()
	const [timestamp, setTimestamp] = useState(new Date())
	//const [returnArray, setReturnArray] = useState([])

	//const leagueId = useParams().leagueId
	const leagueId = '6a466e850a2277ad4c9a7210'

	console.log('inside StandingsBigScreen')
	//
	//
	//
	//
	//
	useEffect(() => {
		const intervalId = setInterval(() => {
			setTimestamp(new Date()) // Update state to trigger a re-render
		}, 60000) // 60000 milliseconds = 1 minute.

		// Cleanup function to clear the interval when the component unmounts
		return () => clearInterval(intervalId)
	}, []) // Empty dependency array ensures the effect runs only once on mount
	//
	//
	//
	//
	//
	//
	//call to getStandings in the league-controller
	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/leagues/${leagueId}/standings`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				if (responseData.listOfTeams[0].divisionName) {
					setDivisionName(responseData.listOfTeams[0].divisionName)
				} else {
					setDivisionName('')
				}

				setMasterArray(responseData.masterMasterArray)
				setLoadedStandings(responseData.listOfTeams)
				setNumberOfTeamsTotal(responseData.numberOfTeamsTotal)
				setLoadedStandingsArray(responseData.listOfTeamsArray)
				setLeagueName(responseData.leagueName)
				setSession(responseData.session)
				setYear(responseData.year)
				setIsLoading(false)
			} catch (err) {
				setError(err.message)
			}
			setIsLoading(false)
		}
		sendRequest()
	}, [leagueId])

	let numberOfTeams

	numberOfTeams = numberOfTeamsTotal && numberOfTeamsTotal.length
	console.log('numberOfTeamsTotal: ' + numberOfTeamsTotal)

	masterArray && masterArray.reverse()
	masterArray && masterArray.splice(numberOfTeamsTotal)
	masterArray && masterArray.reverse()
	let groupedArray,
		girlsU19Array,
		girlsU14Array,
		girlsU11Array,
		boysU15Array,
		boysU13Array,
		boysU11Array
	groupedArray = []
	girlsU19Array = []
	girlsU14Array = []
	girlsU11Array = []
	boysU15Array = []
	boysU13Array = []
	boysU11Array = []
	//
	if (masterArray) {
		const uniqueDivisionsArray = masterArray.map((item) => item.divisionName)

		const toFindUniques = (arry) =>
			arry.filter((item, index) => arry.indexOf(item) === index)

		const uniques = toFindUniques(uniqueDivisionsArray)

		console.log(
			'there are ' + uniques.length + ' divisions.  They are ' + uniques,
		)

		if (uniques.length > 1) {
			console.log(
				'we have more than one division, lets split them up into separate arrays',
			)
			for (const element of uniques) {
				const item = masterArray.filter((el) => el.divisionName === element)
				groupedArray.push(item)
			}

			for (let i = 0; i < groupedArray.length; i++) {
				if (groupedArray[i][0].divisionName === 'Girls U19') {
					girlsU19Array.push(groupedArray[i])
				} else if (groupedArray[i][0].divisionName === 'Girls U14') {
					girlsU14Array.push(groupedArray[i])
				} else if (groupedArray[i][0].divisionName === 'Girls U11') {
					girlsU11Array.push(groupedArray[i])
				} else if (groupedArray[i][0].divisionName === 'Boys U15') {
					boysU15Array.push(groupedArray[i])
				} else if (groupedArray[i][0].divisionName === 'Boys U13') {
					boysU13Array.push(groupedArray[i])
				} else if (groupedArray[i][0].divisionName === 'Boys U11') {
					boysU11Array.push(groupedArray[i])
				}
			}
		}
	}
	//
	//
	//
	//
	//
	const errorHandler = () => {
		setError(null)
	}
	//
	//
	//
	//
	if (groupedArray && groupedArray.length < 1) {
		return (
			<React.Fragment>
				<StatsAndStandingsHomeNavigation
					leagueId={'684374b40c277c1376096064'}
				/>
				<ErrorModal error={error} onClear={errorHandler} />
				{isLoading && (
					<div className='center'>
						<LoadingSpinner />
					</div>
				)}
				{!isLoading && masterArray && (
					<div className='Roster'>
						<h1>Tournament Standings</h1>
						<h1>
							{leagueName} {divisionName && ' - ' + divisionName + ' Division'}
						</h1>
						<h2>
							{session} {year}
						</h2>
						<p style={{ textAlign: 'center' }}>
							(Click on a team to go to their roster and schedule page)
						</p>
						<table id='bigscreenroster' cellSpacing={'0'} cellPadding={'0'}>
							<thead>
								<tr>
									<th style={{ fontSize: 'small' }}>Team</th>
									<th style={{ fontSize: 'small' }}>GP</th>
									<th style={{ fontSize: 'small' }}>W</th>
									<th style={{ fontSize: 'small' }}>L</th>
									<th style={{ fontSize: 'small' }}>T</th>
									<th style={{ fontSize: 'small' }}>GF</th>
									<th style={{ fontSize: 'small' }}>GA</th>
									<th style={{ fontSize: 'small' }}>GD</th>
									<th style={{ fontSize: 'small' }}>Points</th>
									<th style={{ fontSize: 'small' }}>%</th>
								</tr>
							</thead>
							<tbody>
								{masterArray.map((team, key) => {
									return (
										<tr key={key}>
											<td
												className='bigScreentableDataTeamName'
												style={{ fontSize: 'small' }}
											>
												<Link
													style={{
														textDecoration: 'none',
														color: 'black',
														fontWeight: 600,
													}}
													to={`${team.divisionName}/${year}/${team.teamName}`}
												>
													{team.teamName}
												</Link>
											</td>
											<td style={{ fontSize: 'small' }}>
												{team.wins +
													team.losses +
													team.ties +
													team.shootoutLosses +
													team.overtimeLosses}
											</td>
											<td style={{ fontSize: 'small' }}>{team.wins}</td>
											<td style={{ fontSize: 'small' }}>{team.losses}</td>
											<td style={{ fontSize: 'small' }}>{team.ties}</td>
											<td style={{ fontSize: 'small' }}>{team.goalsFor}</td>
											<td style={{ fontSize: 'small' }}>{team.goalsAgainst}</td>
											<td style={{ fontSize: 'small' }}>
												{team.goalsFor - team.goalsAgainst}
											</td>
											<td style={{ fontSize: 'small' }}>{team.points}</td>
											<td style={{ fontSize: 'small' }}>
												{Number(
													(team.wins + team.ties * 0.5) /
														(team.wins +
															team.losses +
															team.overtimeLosses +
															team.shootoutLosses +
															team.ties),
												).toFixed(3)}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
						<table id='bigscreenroster' cellSpacing={'0'} cellPadding={'0'}>
							<thead>
								<tr>
									<th style={{ fontSize: 'small' }}>Team</th>
									<th style={{ fontSize: 'small' }}>GP</th>
									<th style={{ fontSize: 'small' }}>W</th>
									<th style={{ fontSize: 'small' }}>L</th>
									<th style={{ fontSize: 'small' }}>T</th>
									<th style={{ fontSize: 'small' }}>GF</th>
									<th style={{ fontSize: 'small' }}>GA</th>
									<th style={{ fontSize: 'small' }}>GD</th>
									<th style={{ fontSize: 'small' }}>Points</th>
									<th style={{ fontSize: 'small' }}>%</th>
								</tr>
							</thead>
							<tbody>
								{masterArray.map((team, key) => {
									return (
										<tr key={key}>
											<td
												style={{ fontSize: 'small' }}
												className='bigScreentableDataTeamName'
											>
												<Link
													style={{
														textDecoration: 'none',
														color: 'black',
														fontWeight: 600,
													}}
													to={`${team.divisionName}/${year}/${team.teamName}`}
												>
													{team.teamName}
												</Link>
											</td>
											<td style={{ fontSize: 'small' }}>
												{team.wins +
													team.losses +
													team.ties +
													team.shootoutLosses +
													team.overtimeLosses}
											</td>
											<td style={{ fontSize: 'small' }}>{team.wins}</td>
											<td style={{ fontSize: 'small' }}>{team.losses}</td>
											<td style={{ fontSize: 'small' }}>{team.ties}</td>
											<td style={{ fontSize: 'small' }}>{team.goalsFor}</td>
											<td style={{ fontSize: 'small' }}>{team.goalsAgainst}</td>
											<td style={{ fontSize: 'small' }}>
												{team.goalsFor - team.goalsAgainst}
											</td>
											<td style={{ fontSize: 'small' }}>{team.points}</td>
											<td style={{ fontSize: 'small' }}>
												{Number(
													(team.wins + team.ties * 0.5) /
														(team.wins +
															team.losses +
															team.overtimeLosses +
															team.shootoutLosses +
															team.ties),
												).toFixed(3)}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				)}
				{!isLoading && !masterArray && (
					<div className='Standings'>
						<h1>
							{leagueName} {divisionName && divisionName} - Tournament Standings
						</h1>
						<h2>
							{session} {year}
						</h2>
						<table id='players' cellSpacing={'0'} cellPadding={'0'}>
							<thead>
								<tr>
									<th>Team</th>
									<th>GP</th>
									<th>W</th>
									<th>L</th>
									<th>T</th>
									<th>GF</th>
									<th>GA</th>
									<th>Points</th>
								</tr>
							</thead>
						</table>
					</div>
				)}
			</React.Fragment>
		)
	} else if (
		groupedArray &&
		groupedArray.length > 0 &&
		girlsU19Array &&
		girlsU19Array.length > 0 &&
		girlsU14Array &&
		girlsU14Array.length > 0 &&
		girlsU11Array &&
		girlsU11Array.length > 0 &&
		boysU15Array &&
		boysU15Array.length > 0 &&
		boysU13Array &&
		boysU13Array.length > 0 &&
		boysU11Array &&
		boysU11Array.length > 0
	) {
		return (
			<React.Fragment>
				<div className='bigScreenHeader'>
					<div className='bigScreenHeader__logo'>
						<img src='/images/futurestars-logo.png' alt='Future Stars' />
					</div>

					<div className='bigScreenHeader__center'>
						<h1>2026 Future Stars Standings</h1>

						<div className='bigScreenHeader__subtitle'>
							{session} {year}
						</div>
					</div>

					<div className='bigScreenHeader__time'>
						{timestamp.toLocaleTimeString([], {
							hour: 'numeric',
							minute: '2-digit',
						})}
					</div>
				</div>
				<div className='row'>
					<ErrorModal error={error} onClear={errorHandler} />
					{isLoading && (
						<div className='center'>
							<LoadingSpinner />
						</div>
					)}
					<div className='col'>
						{girlsU19Array.map((group) => {
							return (
								<div className='Roster'>
									<h5 style={{ textAlign: 'center', fontWeight: '600' }}>
										{group[0].divisionName} Division
									</h5>
									<table
										id='bigscreenroster'
										cellSpacing={'0'}
										cellPadding={'0'}
									>
										<thead>
											<tr>
												<th style={{ fontSize: 'small' }}>Team</th>
												<th style={{ fontSize: 'small' }}>GP</th>
												<th style={{ fontSize: 'small' }}>W</th>
												<th style={{ fontSize: 'small' }}>L</th>
												<th style={{ fontSize: 'small' }}>SOL</th>
												<th style={{ fontSize: 'small' }}>GF</th>
												<th style={{ fontSize: 'small' }}>GA</th>
												<th style={{ fontSize: 'small' }}>GD</th>
												<th style={{ fontSize: 'small' }}>Points</th>
												<th style={{ fontSize: 'small' }}>%</th>
											</tr>
										</thead>
										<tbody>
											{group.map((team, key) => {
												return (
													<tr key={key}>
														<td
															style={{ fontSize: 'small' }}
															className='bigScreentableDataTeamName'
														>
															<Link
																style={{
																	textDecoration: 'none',
																	color: 'black',
																	fontWeight: 600,
																}}
																to={`${team.divisionName}/${year}/${team.teamName}`}
															>
																{team.teamName}
															</Link>
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.wins +
																team.losses +
																team.ties +
																team.shootoutLosses +
																team.overtimeLosses}
														</td>
														<td style={{ fontSize: 'small' }}>{team.wins}</td>
														<td style={{ fontSize: 'small' }}>{team.losses}</td>
														<td style={{ fontSize: 'small' }}>
															{team.shootoutLosses}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor - team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>{team.points}</td>
														<td style={{ fontSize: 'small' }}>
															{!isNaN(
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3),
															) &&
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)
						})}
						{girlsU14Array.map((group) => {
							return (
								<div className='Roster'>
									<h5 style={{ textAlign: 'center', fontWeight: '600' }}>
										{group[0].divisionName} Division
									</h5>
									<table
										id='bigscreenroster'
										cellSpacing={'0'}
										cellPadding={'0'}
									>
										<thead>
											<tr>
												<th style={{ fontSize: 'small' }}>Team</th>
												<th style={{ fontSize: 'small' }}>GP</th>
												<th style={{ fontSize: 'small' }}>W</th>
												<th style={{ fontSize: 'small' }}>L</th>
												<th style={{ fontSize: 'small' }}>SOL</th>
												<th style={{ fontSize: 'small' }}>GF</th>
												<th style={{ fontSize: 'small' }}>GA</th>
												<th style={{ fontSize: 'small' }}>GD</th>
												<th style={{ fontSize: 'small' }}>Points</th>
												<th style={{ fontSize: 'small' }}>%</th>
											</tr>
										</thead>
										<tbody>
											{group.map((team, key) => {
												return (
													<tr key={key}>
														<td
															style={{ fontSize: 'small' }}
															className='bigScreentableDataTeamName'
														>
															<Link
																style={{
																	textDecoration: 'none',
																	color: 'black',
																	fontWeight: 600,
																}}
																to={`${team.divisionName}/${year}/${team.teamName}`}
															>
																{team.teamName}
															</Link>
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.wins +
																team.losses +
																team.ties +
																team.shootoutLosses +
																team.overtimeLosses}
														</td>
														<td style={{ fontSize: 'small' }}>{team.wins}</td>
														<td style={{ fontSize: 'small' }}>{team.losses}</td>
														<td style={{ fontSize: 'small' }}>
															{team.shootoutLosses}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor - team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>{team.points}</td>
														<td style={{ fontSize: 'small' }}>
															{!isNaN(
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3),
															) &&
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)
						})}
						{girlsU11Array.map((group) => {
							return (
								<div className='Roster'>
									<h5 style={{ textAlign: 'center', fontWeight: '600' }}>
										{group[0].divisionName} Division
									</h5>
									<table
										id='bigscreenroster'
										cellSpacing={'0'}
										cellPadding={'0'}
									>
										<thead>
											<tr>
												<th style={{ fontSize: 'small' }}>Team</th>
												<th style={{ fontSize: 'small' }}>GP</th>
												<th style={{ fontSize: 'small' }}>W</th>
												<th style={{ fontSize: 'small' }}>L</th>
												<th style={{ fontSize: 'small' }}>SOL</th>
												<th style={{ fontSize: 'small' }}>GF</th>
												<th style={{ fontSize: 'small' }}>GA</th>
												<th style={{ fontSize: 'small' }}>GD</th>
												<th style={{ fontSize: 'small' }}>Points</th>
												<th style={{ fontSize: 'small' }}>%</th>
											</tr>
										</thead>
										<tbody>
											{group.map((team, key) => {
												return (
													<tr key={key}>
														<td
															style={{ fontSize: 'small' }}
															className='bigScreentableDataTeamName'
														>
															<Link
																style={{
																	textDecoration: 'none',
																	color: 'black',
																	fontWeight: 600,
																}}
																to={`${team.divisionName}/${year}/${team.teamName}`}
															>
																{team.teamName}
															</Link>
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.wins +
																team.losses +
																team.ties +
																team.shootoutLosses +
																team.overtimeLosses}
														</td>
														<td style={{ fontSize: 'small' }}>{team.wins}</td>
														<td style={{ fontSize: 'small' }}>{team.losses}</td>
														<td style={{ fontSize: 'small' }}>
															{team.shootoutLosses}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor - team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>{team.points}</td>
														<td style={{ fontSize: 'small' }}>
															{!isNaN(
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3),
															) &&
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)
						})}
					</div>
					<div className='col'>
						{boysU15Array.map((group) => {
							return (
								<div className='Roster'>
									<h5 style={{ textAlign: 'center', fontWeight: '600' }}>
										{group[0].divisionName} Division
									</h5>
									<table
										id='bigscreenroster'
										cellSpacing={'0'}
										cellPadding={'0'}
									>
										<thead>
											<tr>
												<th style={{ fontSize: 'small' }}>Team</th>
												<th style={{ fontSize: 'small' }}>GP</th>
												<th style={{ fontSize: 'small' }}>W</th>
												<th style={{ fontSize: 'small' }}>L</th>
												<th style={{ fontSize: 'small' }}>SOL</th>
												<th style={{ fontSize: 'small' }}>GF</th>
												<th style={{ fontSize: 'small' }}>GA</th>
												<th style={{ fontSize: 'small' }}>GD</th>
												<th style={{ fontSize: 'small' }}>Points</th>
												<th style={{ fontSize: 'small' }}>%</th>
											</tr>
										</thead>
										<tbody>
											{group.map((team, key) => {
												return (
													<tr key={key}>
														<td
															style={{ fontSize: 'small' }}
															className='bigScreentableDataTeamName'
														>
															<Link
																style={{
																	textDecoration: 'none',
																	color: 'black',
																	fontWeight: 600,
																}}
																to={`${team.divisionName}/${year}/${team.teamName}`}
															>
																{team.teamName}
															</Link>
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.wins +
																team.losses +
																team.ties +
																team.shootoutLosses +
																team.overtimeLosses}
														</td>
														<td style={{ fontSize: 'small' }}>{team.wins}</td>
														<td style={{ fontSize: 'small' }}>{team.losses}</td>
														<td style={{ fontSize: 'small' }}>
															{team.shootoutLosses}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor - team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>{team.points}</td>
														<td style={{ fontSize: 'small' }}>
															{!isNaN(
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3),
															) &&
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)
						})}
						{boysU13Array.map((group) => {
							return (
								<div className='Roster'>
									<h5 style={{ textAlign: 'center', fontWeight: '600' }}>
										{group[0].divisionName} Division
									</h5>
									<table
										id='bigscreenroster'
										cellSpacing={'0'}
										cellPadding={'0'}
									>
										<thead>
											<tr>
												<th style={{ fontSize: 'small' }}>Team</th>
												<th style={{ fontSize: 'small' }}>GP</th>
												<th style={{ fontSize: 'small' }}>W</th>
												<th style={{ fontSize: 'small' }}>L</th>
												<th style={{ fontSize: 'small' }}>SOL</th>
												<th style={{ fontSize: 'small' }}>GF</th>
												<th style={{ fontSize: 'small' }}>GA</th>
												<th style={{ fontSize: 'small' }}>GD</th>
												<th style={{ fontSize: 'small' }}>Points</th>
												<th style={{ fontSize: 'small' }}>%</th>
											</tr>
										</thead>
										<tbody>
											{group.map((team, key) => {
												return (
													<tr key={key}>
														<td
															style={{ fontSize: 'small' }}
															className='bigScreentableDataTeamName'
														>
															<Link
																style={{
																	textDecoration: 'none',
																	color: 'black',
																	fontWeight: 600,
																}}
																to={`${team.divisionName}/${year}/${team.teamName}`}
															>
																{team.teamName}
															</Link>
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.wins +
																team.losses +
																team.ties +
																team.shootoutLosses +
																team.overtimeLosses}
														</td>
														<td style={{ fontSize: 'small' }}>{team.wins}</td>
														<td style={{ fontSize: 'small' }}>{team.losses}</td>
														<td style={{ fontSize: 'small' }}>
															{team.shootoutLosses}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor - team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>{team.points}</td>
														<td style={{ fontSize: 'small' }}>
															{!isNaN(
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3),
															) &&
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)
						})}
						{boysU11Array.map((group) => {
							return (
								<div className='Roster'>
									<h5 style={{ textAlign: 'center', fontWeight: '600' }}>
										{group[0].divisionName} Division
									</h5>
									<table
										id='bigscreenroster'
										cellSpacing={'0'}
										cellPadding={'0'}
									>
										<thead>
											<tr>
												<th style={{ fontSize: 'small' }}>Team</th>
												<th style={{ fontSize: 'small' }}>GP</th>
												<th style={{ fontSize: 'small' }}>W</th>
												<th style={{ fontSize: 'small' }}>L</th>
												<th style={{ fontSize: 'small' }}>SOL</th>
												<th style={{ fontSize: 'small' }}>GF</th>
												<th style={{ fontSize: 'small' }}>GA</th>
												<th style={{ fontSize: 'small' }}>GD</th>
												<th style={{ fontSize: 'small' }}>Points</th>
												<th style={{ fontSize: 'small' }}>%</th>
											</tr>
										</thead>
										<tbody>
											{group.map((team, key) => {
												return (
													<tr key={key}>
														<td
															style={{ fontSize: 'small' }}
															className='bigScreentableDataTeamName'
														>
															<Link
																style={{
																	textDecoration: 'none',
																	color: 'black',
																	fontWeight: 600,
																}}
																to={`${team.divisionName}/${year}/${team.teamName}`}
															>
																{team.teamName}
															</Link>
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.wins +
																team.losses +
																team.ties +
																team.shootoutLosses +
																team.overtimeLosses}
														</td>
														<td style={{ fontSize: 'small' }}>{team.wins}</td>
														<td style={{ fontSize: 'small' }}>{team.losses}</td>
														<td style={{ fontSize: 'small' }}>
															{team.shootoutLosses}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>
															{team.goalsFor - team.goalsAgainst}
														</td>
														<td style={{ fontSize: 'small' }}>{team.points}</td>
														<td style={{ fontSize: 'small' }}>
															{!isNaN(
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3),
															) &&
																Number(
																	(team.wins + team.ties * 0.5) /
																		(team.wins +
																			team.losses +
																			team.overtimeLosses +
																			team.shootoutLosses +
																			team.ties),
																).toFixed(3)}
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							)
						})}
					</div>
				</div>
			</React.Fragment>
		)
	}
}

export default StandingsBigScreen
 */
